import { useEffect, useMemo, useState } from 'react';
import { X, Eye, EyeOff, Moon, Sun, CheckCircle, Package, Loader } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { getCities } from '../../../redux/actions/city';
import { addCustomer, AddOrder, validateCustomer } from '../../../redux/actions/package';
import { authLogin } from '../../../redux/actions/authentication';
import { userProfile } from '../../../redux/actions/user';
import { GridItem, Image, Select, useColorMode, useColorModeValue, useToast } from '@chakra-ui/react';
import Logo from '../../../assets/img/zeronine.png';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { getPackageForWeb } from '../../../redux/actions/package';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';
import { config } from 'config/config';
import { useUser } from 'contexts';
import { getCompanyDetailsByGst } from '../../../redux/actions/company';

const BusinessSubTypes = {
   MOBILE: 1,
   ELECTRONIC: 2,
};

const RegistrationForm = ({ txnType, userCode, selectedPlanId }) => {
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const packageIdFromUrl = searchParams.get('package');
   const textColor = useColorModeValue('#9E9E9E', 'white');
   const { colorMode, setColorMode } = useColorMode();
   const toast = useToast();
   const isDark = colorMode === 'dark';
   const [errors, setErrors] = useState({});
   const [isLoadingGst, setIsLoadingGst] = useState(false);
   const [gstAutoFilled, setGstAutoFilled] = useState(false);
   const [showPassword, setShowPassword] = useState(false);
   const [showSuccess, setShowSuccess] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [showOtpModal, setShowOtpModal] = useState(false);
   const [otpTimer, setOtpTimer] = useState(60);
   const [canResendOtp, setCanResendOtp] = useState(false);
   const [isResendingOtp, setIsResendingOtp] = useState(false);
   const [otpData, setOtpData] = useState({
      mobileOtp: '',
      emailOtp: '',
   });
   const [customerDataForVerification, setCustomerDataForVerification] = useState(null);
   const [copied, setCopied] = useState(false);
   const [formData, setFormData] = useState({
      name: '',
      mobile: '',
      contactName: '',
      alternativeMobile: '',
      email: '',
      address: '',
      gstin: '',
      limit: '',
      username: '',
      password: '',
      cityId: '',
      package: packageIdFromUrl || '',
      amount: '',
      paymentMode: 'bank',
      referral: '',
      subBusinessType: '',
   });

   const PackageData = useSelector((state) => state.Package?.list || []);
   const cityData = useSelector((state) => state.city?.list || []);
   const companyDetails = useSelector((state) => state.company?.gstDetail || null);

   const selectedPackage = useMemo(() => {
      if (!PackageData.length) return null;

      const packageId = packageIdFromUrl || formData.package;
      if (!packageId) return null;

      return PackageData.find((pkg) => pkg.id === Number(packageId) || pkg._id === packageId || pkg.id === packageId);
   }, [PackageData, packageIdFromUrl, formData.package]);

   // Check if selected package is a mobile package
   const isMobilePackage = useMemo(() => {
      if (!selectedPackage) return false;

      // You can customize this logic based on how you identify mobile packages
      // Examples: check package name, type, category, etc.
      const packageName = selectedPackage.name?.toLowerCase() || '';
      const packageType = selectedPackage.type?.toLowerCase() || '';
      const packageCategory = selectedPackage.category?.toLowerCase() || '';

      return (
         packageName.includes('mobile') ||
         packageType.includes('mobile') ||
         packageCategory.includes('mobile') ||
         selectedPackage.isMobile === true // if you have a specific flag
      );
   }, [selectedPackage]);

   const businessSubTypeOptions = useMemo(
      () => [
         { value: BusinessSubTypes.MOBILE, label: 'Mobile' },
         { value: BusinessSubTypes.ELECTRONIC, label: 'Electronic' },
      ],
      [],
   );
   useEffect(() => {
      if (selectedPackage) {
         const amount = selectedPackage.amcPrice || selectedPackage.price || selectedPackage.amount || '';

         setFormData((prev) => ({
            ...prev,
            package: selectedPackage.id || selectedPackage._id,
            amount: amount,
         }));
      }
   }, [selectedPackage]);

   useEffect(() => {
      if (packageIdFromUrl && PackageData.length > 0) {
         const packageFromUrl = PackageData.find(
            (pkg) => pkg.id === Number(packageIdFromUrl) || pkg._id === packageIdFromUrl || pkg.id === packageIdFromUrl,
         );

         if (packageFromUrl) {
            const amount = packageFromUrl.amcPrice || packageFromUrl.price || packageFromUrl.amount || '';

            setFormData((prev) => ({
               ...prev,
               package: packageFromUrl.id || packageFromUrl._id,
               amount: amount,
            }));
         }
      }
   }, [packageIdFromUrl, PackageData]);

   useEffect(() => {
      const fetchGstDetails = async () => {
         const gstin = formData.gstin?.trim();

         if (!gstin || gstin.length !== 15) {
            return;
         }

         setIsLoadingGst(true);
         setGstAutoFilled(false);

         try {
            const result = await dispatch(getCompanyDetailsByGst(gstin));

            // FIXED: Check if result exists and has the expected structure
            if (result && (result.lgnm || result.tradeNam || result.pradr)) {
               const gstData = result;

               // Extract data from the exact response structure
               const businessName = gstData.lgnm || gstData.tradeNam || '';

               // Build address from the pradr.addr object
               const address = gstData.pradr?.addr || {};
               const addressParts = [
                  address.bno,
                  address.flno,
                  address.bnm,
                  address.st,
                  address.loc,
                  address.dst,
                  address.stcd,
                  address.pncd,
               ].filter(Boolean);

               const fullAddress = addressParts.join(', ');

               // Extract city information
               const cityName = address.dst || address.loc || ''; // "Surat" from dst
               const pincode = address.pncd || ''; // "395002"
               const stateCode = gstin.substring(0, 2); // First 2 digits of GST is state code (24 for Gujarat)

               // Find matching city in cityData
               let matchedCity = null;

               if (Array.isArray(cityData) && cityData.length > 0) {
                  // First, try to find by pincode
                  if (pincode) {
                     matchedCity = cityData.find(
                        (city) =>
                           city.pincode === pincode ||
                           city.postalCode === pincode ||
                           String(city.pincode) === String(pincode),
                     );
                  }

                  // If not found by pincode, try by city name
                  if (!matchedCity && cityName) {
                     const searchCityName = cityName.toLowerCase();
                     matchedCity = cityData.find(
                        (city) =>
                           city.name?.toLowerCase() === searchCityName ||
                           city.name?.toLowerCase().includes(searchCityName) ||
                           searchCityName.includes(city.name?.toLowerCase()),
                     );
                  }

                  // If still not found, try by state code
                  if (!matchedCity && stateCode) {
                     matchedCity = cityData.find(
                        (city) =>
                           String(city.stateCode) === String(stateCode) || city.stateCode === parseInt(stateCode),
                     );
                  }
               }

               // Auto-fill form fields from GST data
               setFormData((prev) => ({
                  ...prev,
                  name: businessName || prev.name,
                  address: fullAddress || prev.address,
                  cityId: matchedCity ? String(matchedCity.id || matchedCity._id) : prev.cityId,
               }));

               setGstAutoFilled(true);

               const filledFields = [];
               if (businessName) filledFields.push('Name');
               if (fullAddress) filledFields.push('Address');
               if (matchedCity) filledFields.push('City');
            } else {
               // Handle error scenarios
               let errorMessage = 'Could not fetch details for this GST number';

               // Check if result has an error message
               if (result && result.message) {
                  errorMessage = result.message;
               } else if (result && result.error) {
                  errorMessage = result.error;
               }
            }
         } catch (error) {
            console.error('GST Fetch Error:', error);
         } finally {
            setIsLoadingGst(false);
         }
      };

      // Debounce GST API call
      const timeoutId = setTimeout(() => {
         if (formData.gstin && formData.gstin.length === 15) {
            fetchGstDetails();
         }
      }, 800); // Wait 800ms after user stops typing

      return () => clearTimeout(timeoutId);
   }, [formData.gstin, dispatch, cityData]);

   const handleChange = (e) => {
      const { name, value, type, checked } = e.target;

      if (name === 'mobile' || name === 'alternativeMobile') {
         const numericValue = value.replace(/\D/g, '');
         setFormData((prev) => ({ ...prev, [name]: numericValue }));
         return;
      }

      if (name === 'email') {
         // पूरा email username में auto-fill करें
         const emailValue = value.trim();

         setFormData((prev) => ({
            ...prev,
            [name]: emailValue,
            username: emailValue, // पूरा email username में डालें
         }));

         return;
      }

      if (name === 'package' && value) {
         const selectedPackage = PackageData.find(
            (pkg) => pkg.id === parseInt(value) || pkg.id === value || pkg._id === value,
         );
         if (selectedPackage) {
            setFormData((prev) => ({
               ...prev,
               [name]: value,
               amount: selectedPackage.amcPrice || selectedPackage.price || selectedPackage.amount || prev.amount,
               subBusinessType: '',
            }));
            return;
         }
      }

      if (name === 'gstin') {
         const cleanValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
         const limitedValue = cleanValue.slice(0, 15);

         setFormData((prev) => ({
            ...prev,
            [name]: limitedValue,
         }));

         if (limitedValue.length === 15) {
            const isValid = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/.test(limitedValue);
            if (!isValid) {
               setErrors((prev) => ({
                  ...prev,
                  gstin: 'Invalid GSTIN. Check format',
               }));
            } else {
               setErrors((prev) => ({ ...prev, gstin: '' }));
            }
         } else {
            setErrors((prev) => ({ ...prev, gstin: '' }));
         }

         return;
      }

      let finalValue = type === 'checkbox' ? checked : value;
      if (name === 'limit') {
         finalValue = value ? parseInt(value) : '';
      }

      setFormData((prev) => ({
         ...prev,
         [name]: finalValue,
      }));
   };

   const handleCityChange = (selectedValue) => {
      let cityId = '';

      if (selectedValue) {
         if (typeof selectedValue === 'object') {
            if (selectedValue.id) {
               cityId = String(selectedValue.id); // Convert to string
            } else if (selectedValue.value) {
               cityId = String(selectedValue.value); // Convert to string
            } else if (Array.isArray(selectedValue) && selectedValue.length > 0) {
               const firstCity = selectedValue[0];
               const extractedId = firstCity.id || firstCity.value || firstCity;
               cityId = String(extractedId); // Convert to string
            }
         } else if (typeof selectedValue === 'string' || typeof selectedValue === 'number') {
            cityId = String(selectedValue); // Convert to string
         }
      }

      setFormData((prev) => ({
         ...prev,
         cityId: cityId, // Now always a string
      }));
   };

   const handleBusinessSubTypeChange = (selectedOption) => {
      // If your Select component returns the whole option object
      const selectedValue = selectedOption ? selectedOption.value : '';

      setFormData((prev) => ({
         ...prev,
         subBusinessType: selectedValue ? String(selectedValue) : '',
      }));
   };

   const handleOtpChange = (e) => {
      const { name, value } = e.target;
      setOtpData((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   useEffect(() => {
      dispatch(getCities());
      dispatch(getPackageForWeb());
   }, [dispatch]);

   useEffect(() => {
      if (selectedPackage?.id) {
         setFormData((prev) => ({
            ...prev,
            package: selectedPackage.id || selectedPackage._id,
            amount: selectedPackage.amcPrice || selectedPackage.price || selectedPackage.amount || '',
         }));
      }
      // const packageId = searchParams.get('package');
      // if (packageId && PackageData.length > 0) {
      //    const selectedPackage = PackageData.find(
      //       (pkg) => pkg.id === parseInt(packageId) || pkg.id === packageId || pkg._id === packageId,
      //    );
      //    if (selectedPackage) {
      //       setFormData((prev) => ({
      //          ...prev,
      //          package: selectedPackage.id || selectedPackage._id,
      //          amount: selectedPackage.amcPrice || selectedPackage.price || selectedPackage.amount || '',
      //       }));
      //    }
      // }
   }, []);

   // Validation functions
   const validateName = (e) => {
      const { name, value } = e.target;

      // Check if the name contains only letters, spaces, and numbers
      const isValid = /^[A-Za-zÀ-ÿ\u00C0-\u017F\s0-9]+$/.test(value);

      if (!isValid && value.trim() !== '') {
         setErrors((prev) => ({
            ...prev,
            [name]: 'Name can only contain letters, numbers and spaces',
         }));
      } else {
         // Clear error if valid
         setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
         });
      }
   };
   const validateGstinFormat = (gstin) => {
      if (!gstin || gstin.length !== 15) {
         return false;
      }

      // GSTIN format: 2 digits, 5 letters, 4 digits, 1 letter, 1 letter/digit, 1 letter, 1 letter/digit
      const gstinPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
      return gstinPattern.test(gstin);
   };
   const validateGstin = () => {
      if (formData.gstin && formData.gstin.length === 15) {
         if (!validateGstinFormat(formData.gstin)) {
            setErrors((prev) => ({
               ...prev,
               gstin: 'Invalid GSTIN format.',
            }));
         } else {
            setErrors((prev) => ({ ...prev, gstin: '' }));
         }
      }
   };

   const cleanFormData = (data) => {
      const cleanedData = {};

      Object.keys(data).forEach((key) => {
         const value = data[key];

         // For subBusinessType, we want to include even if it's 0 or empty string
         if (key === 'subBusinessType') {
            cleanedData[key] = value;
         } else if (value !== '' && value !== null && value !== undefined) {
            if (typeof value === 'string') {
               if (value.trim() !== '') {
                  cleanedData[key] = value;
               }
            } else if (typeof value === 'number') {
               cleanedData[key] = value;
            } else if (typeof value === 'boolean') {
               cleanedData[key] = value;
            }
         }
      });

      return cleanedData;
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      try {
         const customerData = cleanFormData({
            name: formData.name,
            mobile: formData.mobile,
            contactName: formData.contactName,
            contactMobile: formData.alternativeMobile,
            email: formData.email,
            address: formData.address,
            gstin: formData.gstin,
            limit: formData.limit,
            username: formData.username,
            password: formData.password,
            cityId: formData.cityId ? parseInt(formData.cityId) : undefined,
            pId: formData.package,
            amount: formData.amount,
            paymentMode: formData.paymentMode,
            referral: formData.referral,
            subBusinessType: formData.subBusinessType ? parseInt(formData.subBusinessType) : undefined,
         });

         // First validate the customer data
         const validateResult = await dispatch(validateCustomer(customerData));

         // Check if validation was successful but requires OTP verification
         if (validateResult && validateResult.code === 1 && validateResult.data === false) {
            // Show OTP modal for verification
            setCustomerDataForVerification(customerData);
            setShowOtpModal(true);
            setIsSubmitting(false);

            toast({
               title: 'OTP Sent',
               description: validateResult?.message || 'Please check your mobile and email for OTP verification codes.',
               status: 'info',
               duration: 5000,
               isClosable: true,
            });
            return;
         }

         // If validation was successful without OTP requirement, proceed to add customer
         if (validateResult && validateResult.code === 1 && validateResult.data !== false) {
            // await proceedWithCustomerRegistration(customerData);
         } else {
            // Validation failed
            toast({
               title: 'Validation Failed',
               description: validateResult?.message || 'Please check your input data',
               status: 'error',
               duration: 5000,
               isClosable: true,
            });
            setIsSubmitting(false);
         }
      } catch (error) {
         console.error(error);

         toast({
            title: 'Error',
            description: error.message || 'Failed to register customer',
            status: 'error',
            duration: 5000,
            isClosable: true,
         });
         setIsSubmitting(false);
      }
   };

   const handleOtpSubmit = async () => {
      if (!otpData.mobileOtp || !otpData.emailOtp) {
         toast({
            title: 'Missing OTP',
            description: 'Please enter both mobile and email OTP',
            status: 'warning',
            duration: 4000,
            isClosable: true,
         });
         return;
      }

      setIsSubmitting(true);

      try {
         const verifiedCustomerData = {
            ...customerDataForVerification,
            mobileOtp: parseInt(otpData.mobileOtp),
            emailOtp: parseInt(otpData.emailOtp),
         };

         if (selectedPackage?.isDemo) {
            verifiedCustomerData.amount = 0;
         }

         const validateResult = await dispatch(validateCustomer(verifiedCustomerData));

         if (validateResult && validateResult.code === 1) {
            setShowOtpModal(false);

            if (selectedPackage?.isDemo) {
               // Demo package - Direct registration without payment

               toast({
                  title: 'Demo Package Selected',
                  description: 'Processing your registration...',
                  status: 'info',
                  duration: 3000,
                  isClosable: true,
               });

               // Direct registration for demo package - CALL addCustomer
               await proceedWithCustomerRegistration(verifiedCustomerData);
            } else {
               // Paid package - Open Razorpay payment gateway
               const addOrderResult = await dispatch(
                  AddOrder({
                     amount: selectedPackage?.amcPrice || 1,
                  }),
               );

               if (addOrderResult && addOrderResult.code === 1) {
                  const orderId = addOrderResult.data?.orderId || addOrderResult.orderId;

                  if (!orderId) {
                     throw new Error('Order ID not received from server');
                  }

                  // Store customer data in session storage for after payment
                  sessionStorage.setItem('pendingCustomerData', JSON.stringify(verifiedCustomerData));

                  const notes = {
                     txnType: 'NEW',
                     ...(formData.name && { name: formData.name }),
                     ...(formData.mobile && { mobile: formData.mobile }),
                     ...(formData.contactName && { contactName: formData.contactName }),
                     ...(formData.alternativeMobile && { contactMobile: formData.alternativeMobile }),
                     ...(formData.email && { email: formData.email }),
                     ...(formData.address && { address: formData.address }),
                     ...(formData.gstin && { gstin: formData.gstin }),
                     ...(formData.username && { username: formData.username }),
                     ...(formData.password && { password: formData.password }),
                     ...(formData.cityId && { cityId: formData.cityId }),
                     ...(formData.package && { pId: formData.package }),
                     ...(formData.referral && { referral: formData.referral }),
                     ...(formData.subBusinessType && { subBusinessType: formData.subBusinessType }),
                  };

                  const options = {
                     key: config.RZRPAYKEY,
                     amount: (selectedPackage?.amcPrice || formData.amount) * 100,
                     order_id: orderId,
                     name: 'Zeronine Tax',
                     currency: 'INR',
                     description: 'Continue payment to purchase Premium plan',
                     timeout: 600,
                     prefill: {
                        email: formData?.email,
                        contact: formData?.mobile,
                     },
                     theme: {
                        color: '#3399cc',
                     },
                     notes: notes,
                     handler: async function (response) {
                        try {
                           setShowSuccess(true);

                           toast({
                              title: 'Payment Successful!',
                              description: 'Completing your registration...',
                              status: 'success',
                              duration: 3000,
                              isClosable: true,
                           });

                           // ✅ Direct auto-login for paid package (NO addCustomer call)
                           setTimeout(async () => {
                              await handleAutoLogin(verifiedCustomerData);
                           }, 1500);
                        } catch (error) {
                           console.error('Payment processing error:', error);
                           setIsSubmitting(false);
                        }
                     },
                     modal: {
                        ondismiss: function () {
                           setIsSubmitting(false);
                           toast({
                              title: 'Payment Cancelled',
                              description: 'You cancelled the payment',
                              status: 'warning',
                              duration: 3000,
                              isClosable: true,
                           });
                        },
                     },
                  };

                  const rzp = new window.Razorpay(options);

                  rzp.on('payment.failed', function (response) {
                     console.error('❌ Payment Failed!');
                     toast({
                        title: 'Payment Failed',
                        description: response.error.description || 'Something went wrong!',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                     });
                     setIsSubmitting(false);
                  });

                  rzp.open();
               } else {
                  throw new Error('Failed to create order');
               }
            }
         } else {
            toast({
               title: 'OTP Verification Failed',
               description: validateResult?.message || 'Invalid OTP. Please try again.',
               status: 'error',
               duration: 5000,
               isClosable: true,
            });
            setIsSubmitting(false);
         }
      } catch (error) {
         console.error('OTP verification error:', error);
         toast({
            title: 'Error',
            description: 'Failed to verify OTP. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
         });
         setIsSubmitting(false);
      }
   };

   // ONLY for Demo packages (addCustomer call)
   const proceedWithCustomerRegistration = async (customerData) => {
      try {
         const isDemoPackage = selectedPackage?.isDemo;
         const finalAmount = isDemoPackage ? 0 : formData.amount;

         // Create customer data without OTP fields
         const customerDataWithoutOtp = cleanFormData({
            name: formData.name,
            mobile: formData.mobile,
            contactName: formData.contactName,
            contactMobile: formData.alternativeMobile,
            email: formData.email,
            address: formData.address,
            gstin: formData.gstin,
            limit: formData.limit,
            username: formData.username,
            password: formData.password,
            cityId: formData.cityId ? parseInt(formData.cityId) : undefined,
            pId: formData.package,
            amount: finalAmount,
            paymentMode: formData.paymentMode,
            referral: formData.referral,
            subBusinessType: formData.subBusinessType ? parseInt(formData.subBusinessType) : undefined,
         });

         // Add the customer (ONLY for demo)
         const result = await dispatch(addCustomer(customerDataWithoutOtp));

         if (result && result.code === 1) {
            setShowSuccess(true);

            toast({
               title: 'Registration Successful!',
               description: 'Logging you in...',
               status: 'success',
               duration: 2000,
               isClosable: true,
            });

            // Auto-login after 1 second
            setTimeout(async () => {
               await handleAutoLogin(customerDataWithoutOtp);
            }, 1000);
         } else if (result && result.code === 2) {
            toast({
               title: 'Warning',
               description: result.message || 'Please check your information',
               status: 'warning',
               duration: 4000,
               isClosable: true,
            });
         } else {
            throw new Error(result?.message || 'Failed to register customer');
         }
      } catch (error) {
         console.error('Registration error:', error);
         toast({
            title: 'Error',
            description: error.message || 'Failed to register customer',
            status: 'error',
            duration: 5000,
            isClosable: true,
         });
      } finally {
         setIsSubmitting(false);
      }
   };

   // Common auto-login function for both demo and paid packages
   const handleAutoLogin = async (customerData) => {
      try {
         // Clear any existing token before auto-login
         localStorage.removeItem('authTokenCustomer');

         const loginData = {
            email: customerData.email,
            password: customerData.password,
         };

         // Create navigation callback for authLogin
         const handleLoginRedirect = (redirectPath) => {
            if (redirectPath === '/auth/create-comp') {
               toast({
                  title: 'Welcome!',
                  description: 'Please create your company to continue',
                  status: 'info',
                  duration: 3000,
                  isClosable: true,
               });
            } else {
               toast({
                  title: 'Welcome Back!',
                  description: 'Redirecting to dashboard...',
                  status: 'success',
                  duration: 2000,
                  isClosable: true,
               });
            }

            setTimeout(() => {
               navigate(redirectPath, { replace: true });
            }, 1000);
         };

         // Call authLogin with navigation callback
         const loginResult = await dispatch(authLogin(handleLoginRedirect, loginData));

         if (loginResult && loginResult.code === 1) {
            // IMPORTANT: Wait for Redux state to update
            await new Promise((resolve) => setTimeout(resolve, 300));

            // Fetch user profile
            await dispatch(userProfile());

            // Broadcast login event
            const authChannel = new BroadcastChannel('auth');
            authChannel.postMessage({ type: 'LOGIN' });
            authChannel.close();

            // Clear temporary data if exists
            sessionStorage.removeItem('pendingCustomerData');

            // Note: Navigation is handled by the callback function
         } else {
            // Login failed after registration
            console.error('Registration auto-login failed:', loginResult);
            toast({
               title: selectedPackage?.isDemo ? 'Registration Successful' : 'Payment Successful',
               description: 'Please login manually',
               status: 'warning',
               duration: 3000,
               isClosable: true,
            });

            setTimeout(() => {
               setShowSuccess(false);
               navigate('/auth/sign-in');
            }, 2000);
         }
      } catch (loginError) {
         console.error('Registration auto-login error:', loginError);
         toast({
            title: selectedPackage?.isDemo ? 'Registration Successful' : 'Payment Successful',
            description: 'Please login manually',
            status: 'warning',
            duration: 3000,
            isClosable: true,
         });

         setTimeout(() => {
            setShowSuccess(false);
            navigate('/auth/sign-in');
         }, 2000);
      }
   };

   const handleCancel = () => {
      setFormData({
         name: '',
         mobile: '',
         contactName: '',
         alternativeMobile: '',
         email: '',
         address: '',
         gstin: '',
         limit: '',
         username: '',
         password: '',
         cityId: '',
         package: '',
         amount: '',
         paymentMode: 'bank',
         referral: '',
         subBusinessType: '', // Reset businessSubType
      });
      setShowSuccess(false);
      setShowOtpModal(false);
      setOtpData({
         mobileOtp: '',
         emailOtp: '',
      });
   };

   const handleResendOtp = async () => {
      if (!canResendOtp || isResendingOtp) return;

      setIsResendingOtp(true);

      try {
         // Clear existing OTP fields
         setOtpData({
            mobileOtp: '',
            emailOtp: '',
         });

         // Call the validateCustomer API again without OTP to trigger resend
         const resendResult = await dispatch(validateCustomer(customerDataForVerification));

         if (resendResult && resendResult.code === 1) {
            toast({
               title: 'OTP Resent',
               description: 'New OTPs have been sent to your mobile and email.',
               status: 'success',
               duration: 4000,
               isClosable: true,
            });

            // Reset timer
            setOtpTimer(60);
            setCanResendOtp(false);

            // Start timer again
            const timerInterval = setInterval(() => {
               setOtpTimer((prev) => {
                  if (prev <= 1) {
                     clearInterval(timerInterval);
                     setCanResendOtp(true);
                     return 0;
                  }
                  return prev - 1;
               });
            }, 1000);
         } else {
            toast({
               title: 'Failed to Resend OTP',
               description: resendResult?.message || 'Please try again later.',
               status: 'error',
               duration: 4000,
               isClosable: true,
            });
         }
      } catch (error) {
         console.error('Resend OTP error:', error);
         toast({
            title: 'Error',
            description: 'Failed to resend OTP. Please try again.',
            status: 'error',
            duration: 4000,
            isClosable: true,
         });
      } finally {
         setIsResendingOtp(false);
      }
   };

   // ✅ Start timer when OTP modal opens
   useEffect(() => {
      if (showOtpModal) {
         setOtpTimer(60);
         setCanResendOtp(false);

         const timerInterval = setInterval(() => {
            setOtpTimer((prev) => {
               if (prev <= 1) {
                  clearInterval(timerInterval);
                  setCanResendOtp(true);
                  return 0;
               }
               return prev - 1;
            });
         }, 1000);

         return () => clearInterval(timerInterval);
      }
   }, [showOtpModal]);

   const closeOtpModal = () => {
      setShowOtpModal(false);
      setOtpData({
         mobileOtp: '',
         emailOtp: '',
      });
      setOtpTimer(60);
      setCanResendOtp(false);
      setIsSubmitting(false);
   };
   const handleNumericKeyDown = (e) => {
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (allowedKeys.includes(e.key)) return;
      if (!/^\d$/.test(e.key)) {
         e.preventDefault();
      }
   };

   return (
      <div
         className={`min-h-screen transition-colors duration-300 ${isDark
            ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
            : 'bg-gradient-to-br from-blue-50 via-white to-gray-100'
            }`}>
         {/* Header Section */}
         <div
            className={`border-b ${isDark ? 'border-slate-700 bg-slate-800/80' : 'border-blue-200 bg-white/80'
               } backdrop-blur-sm`}>
            <div className="max-w-7xl mx-auto px-6 py-4">
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <Link to={`/`}>
                        <Image src={Logo} alt="ZeroNine Logo" width={'200px'} />{' '}
                     </Link>
                  </div>
                  <div className="flex items-center gap-4">
                     <button
                        onClick={() => setColorMode(isDark ? 'light' : 'dark')}
                        className={`p-3 rounded-xl transition-all duration-300 ${isDark
                           ? 'bg-slate-700 hover:bg-slate-600 border border-slate-600'
                           : 'bg-white hover:bg-blue-50 border border-blue-200 shadow-sm'
                           }`}>
                        {isDark ? (
                           <Sun className="w-5 h-5 text-amber-400" />
                        ) : (
                           <Moon className="w-5 h-5 text-blue-600" />
                        )}
                     </button>
                  </div>
               </div>
            </div>
         </div>

         {/* OTP Verification Modal */}
         {showOtpModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
               <div
                  className={`rounded-2xl shadow-xl w-full max-w-md ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-blue-100'
                     } backdrop-blur-sm`}>
                  {/* Modal Header */}
                  <div className={`p-6 border-b ${isDark ? 'border-slate-700' : 'border-blue-100'}`}>
                     <div className="flex items-center justify-between">
                        <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                           OTP Verification
                        </h3>
                        <button
                           onClick={closeOtpModal}
                           className={`p-2 rounded-lg transition-colors ${isDark
                              ? 'text-slate-400 hover:text-slate-300 hover:bg-slate-700'
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                              }`}
                           disabled={isSubmitting}>
                           <X className="w-5 h-5" />
                        </button>
                     </div>
                     <p className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                        Please enter the OTPs sent to your mobile and email for verification.
                     </p>
                     <div className="mt-4 flex items-center justify-between">
                        <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                           Didn't receive OTP?
                        </span>
                        <button
                           type="button"
                           onClick={handleResendOtp}
                           disabled={!canResendOtp || isResendingOtp}
                           className={`text-sm font-medium ${canResendOtp
                              ? isDark
                                 ? 'text-blue-400 hover:text-blue-300'
                                 : 'text-blue-600 hover:text-blue-800'
                              : isDark
                                 ? 'text-slate-500'
                                 : 'text-gray-400'
                              } disabled:cursor-not-allowed`}>
                           {isResendingOtp ? 'Sending...' : canResendOtp ? 'Resend OTP' : `Resend in ${otpTimer}s`}
                        </button>
                     </div>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6">
                     <div className="space-y-6">
                        {/* Mobile OTP */}
                        <div>
                           <label
                              className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'
                                 }`}>
                              Mobile OTP <span className="text-red-500">*</span>
                           </label>
                           <div className="flex gap-2 justify-center">
                              {[0, 1, 2, 3, 4, 5].map((index) => (
                                 <input
                                    key={`mobile-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={otpData.mobileOtp[index] || ''}
                                    onChange={(e) => {
                                       const value = e.target.value.replace(/[^0-9]/g, '');
                                       if (value.length <= 1) {
                                          const newOtp = otpData.mobileOtp.split('');
                                          newOtp[index] = value;
                                          setOtpData((prev) => ({ ...prev, mobileOtp: newOtp.join('') }));

                                          // Auto-focus next input
                                          if (value && index < 5) {
                                             const nextInput = document.querySelector(
                                                `input[name="mobile-otp-${index + 1}"]`,
                                             );
                                             if (nextInput) nextInput.focus();
                                          }
                                       }
                                    }}
                                    onKeyDown={(e) => {
                                       // Handle backspace to go to previous input
                                       if (e.key === 'Backspace' && !otpData.mobileOtp[index] && index > 0) {
                                          const prevInput = document.querySelector(
                                             `input[name="mobile-otp-${index - 1}"]`,
                                          );
                                          if (prevInput) prevInput.focus();
                                       }
                                    }}
                                    name={`mobile-otp-${index}`}
                                    className={`w-12 h-14 text-center text-xl font-semibold rounded-xl border-2 transition-all duration-200 ${isDark
                                       ? 'bg-slate-900/50 border-slate-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                       : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                       } focus:outline-none`}
                                    disabled={isSubmitting}
                                 />
                              ))}
                           </div>
                        </div>

                        {/* Email OTP */}
                        <div>
                           <label
                              className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'
                                 }`}>
                              Email OTP <span className="text-red-500">*</span>
                           </label>
                           <div className="flex gap-2 justify-center">
                              {[0, 1, 2, 3, 4, 5].map((index) => (
                                 <input
                                    key={`email-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={otpData.emailOtp[index] || ''}
                                    onChange={(e) => {
                                       const value = e.target.value.replace(/[^0-9]/g, '');
                                       if (value.length <= 1) {
                                          const newOtp = otpData.emailOtp.split('');
                                          newOtp[index] = value;
                                          setOtpData((prev) => ({ ...prev, emailOtp: newOtp.join('') }));

                                          // Auto-focus next input
                                          if (value && index < 5) {
                                             const nextInput = document.querySelector(
                                                `input[name="email-otp-${index + 1}"]`,
                                             );
                                             if (nextInput) nextInput.focus();
                                          }
                                       }
                                    }}
                                    onKeyDown={(e) => {
                                       // Handle backspace to go to previous input
                                       if (e.key === 'Backspace' && !otpData.emailOtp[index] && index > 0) {
                                          const prevInput = document.querySelector(
                                             `input[name="email-otp-${index - 1}"]`,
                                          );
                                          if (prevInput) prevInput.focus();
                                       }
                                    }}
                                    name={`email-otp-${index}`}
                                    className={`w-12 h-14 text-center text-xl font-semibold rounded-xl border-2 transition-all duration-200 ${isDark
                                       ? 'bg-slate-900/50 border-slate-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                       : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                       } focus:outline-none`}
                                    disabled={isSubmitting}
                                 />
                              ))}
                           </div>
                        </div>

                        <div className="flex gap-3 justify-end">
                           <button
                              type="button"
                              onClick={closeOtpModal}
                              disabled={isSubmitting}
                              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${isDark
                                 ? 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
                                 : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm'
                                 } disabled:opacity-50 disabled:cursor-not-allowed`}>
                              Cancel
                           </button>
                           <button
                              type="button"
                              onClick={handleOtpSubmit}
                              disabled={isSubmitting}
                              className="px-6 py-3 rounded-xl font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 shadow-lg hover:shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                              {isSubmitting ? (
                                 <>
                                    <svg
                                       className="animate-spin h-5 w-5"
                                       xmlns="http://www.w3.org/2000/svg"
                                       fill="none"
                                       viewBox="0 0 24 24">
                                       <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"></circle>
                                       <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Verifying...
                                 </>
                              ) : (
                                 'Verify & Submit'
                              )}
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         )}

         {/* Main Content */}
         <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Success Message */}
            {showSuccess && (
               <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-top duration-300">
                  <div
                     className={`flex items-center gap-3 px-6 py-4 rounded-xl shadow-lg border ${isDark ? 'bg-green-900/90 border-green-700 backdrop-blur-sm' : 'bg-green-50 border-green-200'
                        }`}>
                     <CheckCircle className="w-5 h-5 text-green-600" />
                     <div>
                        <p className={`font-semibold ${isDark ? 'text-green-100' : 'text-green-900'}`}>
                           Customer added successfully!
                        </p>
                        <p className={`text-sm ${isDark ? 'text-green-200' : 'text-green-700'}`}>
                           Logging in automatically...
                        </p>
                     </div>
                  </div>
               </div>
            )}

            <div className="flex gap-8">
               {/* Form Section */}
               <div className="mx-auto">
                  <div
                     className={`rounded-2xl mx-auto shadow-xl overflow-hidden ${isDark ? 'bg-slate-800/80 border border-slate-700' : 'bg-white/80 border border-blue-100'
                        } backdrop-blur-sm`}>
                     {/* Form Header */}
                     <div className={`p-6 border-b ${isDark ? 'border-slate-700' : 'border-blue-100'}`}>
                        <div className="flex items-center justify-between">
                           <div>
                              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                 Registration
                              </h2>
                           </div>
                           {selectedPackage && (
                              <div
                                 className={`px-4 py-3 rounded-lg border ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-blue-50 border-blue-200'
                                    }`}>
                                 <div className="flex items-center space-x-3">
                                    <div className={`p-2 rounded-full ${isDark ? 'bg-slate-700' : 'bg-blue-100'}`}>
                                       <Package className={isDark ? 'text-slate-300' : 'text-blue-600'} size={24} />
                                    </div>
                                    <div className="flex-1">
                                       {/* Package Name Row */}
                                       <div className="flex items-center justify-between">
                                          <div
                                             className={`flex items-center gap-2 ${isDark ? 'text-slate-200' : 'text-blue-800'
                                                }`}>
                                             <span className="text-sm font-medium">Selected Package:</span>
                                             <span className="text-xl font-bold">{selectedPackage.name}</span>
                                          </div>

                                          {/* Demo Package Badge - Only show if it's a demo package */}
                                          {selectedPackage.isDemo && (
                                             <span
                                                className={`px-3 py-1 rounded-full text-xs font-bold ${isDark
                                                   ? 'bg-green-900/30 text-green-300 border border-green-700'
                                                   : 'bg-green-100 text-green-800 border border-green-300'
                                                   }`}>
                                                DEMO
                                             </span>
                                          )}
                                       </div>

                                       {/* Amount Row - Only show if NOT a demo package */}
                                       {!selectedPackage.isDemo && formData.amount && (
                                          <div
                                             className={`flex items-center gap-2 mt-1 ${isDark ? 'text-slate-200' : 'text-blue-800'
                                                }`}>
                                             <span className="text-sm font-medium">Amount:</span>
                                             <span className="text-xl font-bold">₹{formData.amount}</span>
                                          </div>
                                       )}

                                       {/* Demo Package Message - Show special message for demo packages */}
                                       {selectedPackage.isDemo && (
                                          <div className="mt-1">
                                             <div
                                                className={`flex items-center gap-2 ${isDark ? 'text-green-300' : 'text-green-700'
                                                   }`}>
                                                <span className="text-sm font-medium">Package Type:</span>
                                                <span className="text-sm font-bold">Free Demo Version</span>
                                             </div>
                                             <p
                                                className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'
                                                   }`}>
                                                This is a free demo package with all features enabled for testing
                                             </p>
                                          </div>
                                       )}

                                       {/* Mobile Package Indicator */}
                                       {isMobilePackage && !selectedPackage.isDemo && (
                                          <div
                                             className={`flex items-center gap-2 mt-1 ${isDark ? 'text-slate-200' : 'text-blue-800'
                                                }`}>
                                             <span className="text-sm font-medium">Type:</span>
                                             <span className="text-sm font-bold">Mobile Package</span>
                                          </div>
                                       )}

                                       {/* Mobile Package + Demo */}
                                       {isMobilePackage && selectedPackage.isDemo && (
                                          <div
                                             className={`flex items-center gap-2 mt-1 ${isDark ? 'text-slate-200' : 'text-blue-800'
                                                }`}>
                                             <span className="text-sm font-medium">Type:</span>
                                             <span className="text-sm font-bold">Mobile (Demo)</span>
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              </div>
                           )}
                        </div>
                     </div>

                     {/* Form Content */}
                     <div className="p-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                           {/* Personal Information Section */}
                           <div>
                              <h3
                                 className={`text-lg font-semibold mb-4 pb-2 border-b ${isDark ? 'border-slate-700 text-blue-400' : 'border-blue-100 text-blue-600'
                                    }`}>
                                 Personal Information
                              </h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                 {/* GSTIN Field with Auto-fill indicator */}
                                 <div>
                                    <label
                                       className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'
                                          }`}>
                                       GSTIN
                                       {/* <span className="text-red-500">*</span> */}
                                    </label>
                                    <div className="relative">
                                       <input
                                          type="text"
                                          name="gstin"
                                          value={formData.gstin}
                                          onChange={handleChange}
                                          onBlur={validateGstin} // Add blur validation
                                          placeholder="Enter GSTIN (15 characters)"
                                          maxLength={15}
                                          className={`w-full px-4 py-3 pr-10 rounded-xl border transition-all duration-200 ${isDark
                                             ? 'bg-slate-900/50 border-slate-600 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                             : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10'
                                             } ${errors.gstin ? 'border-red-500' : ''} focus:outline-none uppercase`}
                                          disabled={isSubmitting || isLoadingGst}
                                       />
                                       {errors.gstin && <p className="mt-1 text-sm text-red-500">{errors.gstin}</p>}
                                    </div>
                                 </div>

                                 {/* Other fields */}
                                 {[
                                    {
                                       label: 'Company Name',
                                       name: 'name',
                                       type: 'text',
                                       required: true,
                                       placeholder: 'Enter full name',
                                    },
                                    {
                                       label: 'Mobile',
                                       name: 'mobile',
                                       type: 'tel',
                                       required: true,
                                       pattern: '[0-9]{10}',
                                       inputMode: 'numeric',
                                       maxLength: 10,
                                       placeholder: 'Enter mobile number',
                                    },
                                    {
                                       label: 'Contact Name',
                                       name: 'contactName',
                                       type: 'text',
                                       required: true,
                                       placeholder: 'Enter contact name',
                                    },
                                    {
                                       label: 'Alternative Mobile',
                                       name: 'alternativeMobile',
                                       type: 'tel',
                                       pattern: '[0-9]{0,10}',
                                       inputMode: 'numeric',
                                       maxLength: 10,
                                       placeholder: 'Enter alternative number',
                                    },
                                    {
                                       label: 'Email',
                                       name: 'email',
                                       type: 'email',
                                       required: true,
                                       placeholder: 'Enter email',
                                    },
                                 ].map((field) => (
                                    <div key={field.name}>
                                       <label
                                          className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'
                                             }`}>
                                          {field.required && <span className="text-red-500">* </span>}
                                          {field.label}
                                       </label>
                                       <input
                                          type={field.type}
                                          name={field.name}
                                          value={formData[field.name]}
                                          onChange={handleChange}
                                          onKeyDown={
                                             field.name === 'mobile' || field.name === 'alternativeMobile'
                                                ? handleNumericKeyDown
                                                : undefined
                                          }
                                          onBlur={
                                             field.name === 'name' || field.name === 'contactName'
                                                ? validateName
                                                : undefined
                                          } // Add blur for name fields
                                          className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${isDark
                                             ? 'bg-slate-900/50 border-slate-600 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                             : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10'
                                             } ${errors[field.name] ? 'border-red-500' : ''} focus:outline-none`}
                                          required={field.required}
                                          disabled={isSubmitting}
                                          pattern={field.pattern}
                                          inputMode={field.inputMode}
                                          maxLength={field.maxLength}
                                          placeholder={field.placeholder}
                                       />
                                       {errors[field.name] && (
                                          <p className="mt-1 text-sm text-red-500">{errors[field.name]}</p>
                                       )}
                                    </div>
                                 ))}
                              </div>
                           </div>

                           {/* Address Section */}
                           <div>
                              <h3
                                 className={`text-lg font-semibold mb-4 pb-2 border-b ${isDark ? 'border-slate-700 text-blue-400' : 'border-blue-100 text-blue-600'
                                    }`}>
                                 Address Information
                              </h3>
                              <div className="grid grid-cols-1 gap-6">
                                 <div>
                                    <label
                                       className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'
                                          }`}>
                                       <span className="text-red-500">* </span>Address
                                    </label>
                                    <input
                                       type="text"
                                       name="address"
                                       value={formData.address}
                                       onChange={handleChange}
                                       placeholder="Enter Address"
                                       className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${isDark
                                          ? 'bg-slate-900/50 border-slate-600 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10'
                                          } focus:outline-none`}
                                       required
                                       disabled={isSubmitting}
                                    />
                                 </div>
                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <GridItem>
                                       <div>
                                          {renderDynamicDropdown(
                                             'cityId',
                                             'City',
                                             cityData,
                                             {
                                                value: formData.cityId,
                                                onChange: handleCityChange,
                                                showCustomNoOptions: true,
                                                openfrom: '',
                                                autoComplete: 'off',
                                             },
                                             {},
                                             textColor,
                                             true,
                                             'Select Item',
                                             true,
                                             undefined,
                                             false,
                                          )}
                                       </div>
                                    </GridItem>
                                 </div>
                              </div>
                           </div>

                           {/* Business Sub Type Section - Only show for mobile packages */}
                           {isMobilePackage && (
                              <div>
                                 <h3
                                    className={`text-lg font-semibold mb-4 pb-2 border-b ${isDark ? 'border-slate-700 text-blue-400' : 'border-blue-100 text-blue-600'
                                       }`}>
                                    Business Type
                                 </h3>
                                 <div>
                                    <label
                                       className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'
                                          }`}>
                                       Business Sub Type <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                       value={formData.subBusinessType}
                                       onChange={(e) => {
                                          const selectedValue = e.target.value;
                                          setFormData((prev) => ({
                                             ...prev,
                                             subBusinessType: selectedValue,
                                          }));
                                       }}
                                       placeholder="Select Business Sub Type"
                                       disabled={isSubmitting}
                                       required={isMobilePackage}
                                       className={`w-full rounded-xl border transition-all duration-200 ${isDark
                                          ? 'bg-slate-900/50 border-slate-600 text-white'
                                          : 'bg-white border-gray-300 text-gray-900'
                                          }`}
                                       sx={{
                                          '& option': {
                                             bg: isDark ? 'slate.900' : 'white',
                                             color: isDark ? 'white' : 'gray.900',
                                          },
                                       }}>
                                       {businessSubTypeOptions.map((option) => (
                                          <option key={option.value} value={option.value}>
                                             {option.label}
                                          </option>
                                       ))}
                                    </Select>
                                 </div>
                              </div>
                           )}

                           {/* Account Information Section */}
                           <div>
                              <h3
                                 className={`text-lg font-semibold mb-4 pb-2 border-b ${isDark ? 'border-slate-700 text-blue-400' : 'border-blue-100 text-blue-600'
                                    }`}>
                                 Account Information
                              </h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                 <div>
                                    <label
                                       className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                       <span className="text-red-500">* </span>Username
                                    </label>
                                    <div className="relative">
                                       <input
                                          type="text"
                                          name="username"
                                          value={formData.username}
                                          readOnly
                                          className={`w-full px-4 py-3 pr-12 rounded-xl border transition-all duration-200 ${isDark
                                             ? 'bg-slate-800 border-slate-600 text-slate-300 cursor-not-allowed'
                                             : 'bg-gray-100 border-gray-300 text-gray-700 cursor-not-allowed'
                                             } focus:outline-none`}
                                          placeholder="Will auto-fill from email"
                                       />
                                       <button
                                          type="button"
                                          onClick={() => {
                                             if (formData.username) {
                                                navigator.clipboard.writeText(formData.username);
                                                setCopied(true);
                                                setTimeout(() => setCopied(false), 2000);
                                             }
                                          }}
                                          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-all duration-200 ${isDark
                                             ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-700'
                                             : 'text-gray-400 hover:text-blue-500 hover:bg-gray-200'
                                             }`}
                                          title="Copy username">
                                          {copied ? (
                                             <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                             </svg>
                                          ) : (
                                             <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                             </svg>
                                          )}
                                       </button>
                                    </div>
                                 </div>
                                 <div>
                                    <label
                                       className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'
                                          }`}>
                                       <span className="text-red-500">* </span>Password
                                    </label>
                                    <div className="relative">
                                       <input
                                          type={showPassword ? 'text' : 'password'}
                                          name="password"
                                          value={formData.password}
                                          onChange={handleChange}
                                          className={`w-full px-4 py-3 pr-12 rounded-xl border transition-all duration-200 ${isDark
                                             ? 'bg-slate-900/50 border-slate-600 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                             : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10'
                                             } focus:outline-none`}
                                          placeholder="••••••"
                                          minLength={8}
                                          required
                                          disabled={isSubmitting}
                                       />
                                       <button
                                          type="button"
                                          onClick={() => setShowPassword(!showPassword)}
                                          className={`absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors ${isDark
                                             ? 'text-slate-400 hover:text-slate-300'
                                             : 'text-gray-400 hover:text-gray-600'
                                             }`}
                                          disabled={isSubmitting}>
                                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                       </button>
                                    </div>
                                 </div>
                                 <div>
                                    <label
                                       className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'
                                          }`}>
                                       Referral By (Optional)
                                    </label>
                                    <input
                                       type="text"
                                       name="referral"
                                       value={formData.referral}
                                       onChange={handleChange}
                                       className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${isDark
                                          ? 'bg-slate-900/50 border-slate-600 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10'
                                          } focus:outline-none`}
                                       placeholder="Enter referral code or name"
                                       disabled={isSubmitting}
                                    />
                                 </div>
                              </div>
                           </div>

                           {/* Action Buttons */}
                           <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-slate-700">
                              <Link to={'/'}>
                                 <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={isSubmitting}
                                    className={`px-8 py-3 rounded-xl font-semibold transition-all duration-200 ${isDark
                                       ? 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
                                       : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm'
                                       } disabled:opacity-50 disabled:cursor-not-allowed`}>
                                    Cancel
                                 </button>
                              </Link>
                              <button
                                 type="submit"
                                 disabled={isSubmitting}
                                 className="px-8 py-3 rounded-xl font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 shadow-lg hover:shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                                 {isSubmitting ? (
                                    <>
                                       <svg
                                          className="animate-spin h-5 w-5"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24">
                                          <circle
                                             className="opacity-25"
                                             cx="12"
                                             cy="12"
                                             r="10"
                                             stroke="currentColor"
                                             strokeWidth="4"></circle>
                                          <path
                                             className="opacity-75"
                                             fill="currentColor"
                                             d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                       </svg>
                                       Creating...
                                    </>
                                 ) : (
                                    'Save'
                                 )}
                              </button>
                           </div>
                        </form>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default RegistrationForm;
